import { lazy, Suspense, useEffect, useMemo } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, CssBaseline, CircularProgress, Box } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { getTheme } from '@/theme';
import { useUIStore } from '@/stores/uiStore';
import ErrorBoundary from '@/components/ErrorBoundary';
import { SnackbarProvider } from 'notistack';
import CookieConsent from '@/components/CookieConsent';
import AppShell from '@/components/layout/AppShell';
import OnboardingShell from '@/components/layout/OnboardingShell';
import PlatformShell from '@/components/layout/PlatformShell';
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import LoginPage from '@/pages/auth/LoginPage';
import Auth0CallbackPage from '@/pages/auth/Auth0CallbackPage';
import DepartmentSettingsPage from '@/pages/settings/DepartmentSettingsPage';
import GroupSettingsPage from '@/pages/settings/GroupSettingsPage';
import OnboardingSettingsPage from '@/pages/settings/OnboardingSettingsPage';
import HolidaysPage from '@/pages/settings/HolidaysPage';
import AnnouncementsPage from '@/pages/settings/AnnouncementsPage';
import NotificationsSettingsPage from '@/pages/settings/NotificationsPage';
import CompanySettingsPage from '@/pages/settings/CompanySettingsPage';
import UsersPage from '@/pages/settings/UsersPage';
import RolesPage from '@/pages/settings/RolesPage';
import RoleDetailPage from '@/pages/settings/RoleDetailPage';
import ReferenceDataPage from '@/pages/settings/ReferenceDataPage';
import ProfilePage from '@/pages/profile/ProfilePage';
import PaymentDetailsPage from '@/pages/employee/PaymentDetailsPage';
import AuditLogPage from '@/pages/audit/AuditLogPage';
import NotificationsPage from '@/pages/notifications/NotificationsPage';
import MessagingPage from '@/pages/messaging/MessagingPage';
import ModuleConfigPage from '@/pages/settings/ModuleConfigPage';
import PortalPage from '@/pages/portal/PortalPage';
import MyOnboardingPage from '@/pages/portal/MyOnboardingPage';
import AnnouncementDetailPage from '@/pages/portal/AnnouncementDetailPage';

// Lazy-loaded pages
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage'));
const EmployeeDetailPage = lazy(() => import('@/pages/hr/EmployeeDetailPage'));
// Sprint 3 cut-over COMPLETE (2026-04-23). Every /platform/* route in the
// tenant app now unconditionally renders the "moved to console.anoorehr.com"
// page. The 22 legacy pages/platform/*.tsx source files were deleted
// 2026-05-15 once console feature parity was confirmed. Only the redirect
// page and SSOConnectionsPage (still tenant-scoped) remain under
// pages/platform/. Nginx 301s at the edge for bookmarks; this route handles
// any direct-hit paths inside the SPA.
const PlatformMovedToConsolePage = lazy(() => import('@/pages/platform/MovedToConsolePage'));
const SSOConnectionsPage = lazy(() => import('@/pages/platform/auth/SSOConnectionsPage'));
const AISettingsPage = lazy(() => import('@/pages/settings/AISettingsPage'));
const CommsComplianceDashboardPage = lazy(() => import('@/pages/comms/ComplianceDashboardPage'));
const DeliverabilityPage = lazy(() => import('@/pages/comms/DeliverabilityPage'));
const ApplicantDetailPage = lazy(() => import('@/pages/hr/ApplicantDetailPage'));
const RecruitmentSettingsPage = lazy(() => import('@/pages/hr/RecruitmentSettingsPage'));
const TrackingCategoriesPage = lazy(() => import('@/pages/settings/TrackingCategoriesPage'));
const DataImportPage = lazy(() => import('@/pages/settings/DataImportPage'));
const DataMigrationWizardPage = lazy(() => import('@/pages/settings/DataMigrationWizardPage'));
const MyApprovalsPage = lazy(() => import('@/pages/approvals/MyApprovalsPage'));
const SetupWizardPage = lazy(() => import('@/pages/settings/SetupWizardPage'));
const GettingStartedPage = lazy(() => import('@/pages/getting-started/GettingStartedPage'));
const AutomationsPage = lazy(() => import('@/pages/settings/AutomationsPage'));
const WorkflowsPage = lazy(() => import('@/pages/settings/WorkflowsPage'));
const WorkflowBuilderPage = lazy(() => import('@/pages/workflows/WorkflowBuilderPage'));
const SettingsHubPage = lazy(() => import('@/pages/settings/SettingsHubPage'));
const PerformanceReviewsPage = lazy(() => import('@/pages/hr/PerformanceReviewsPage'));
const CycleDetailPage = lazy(() => import('@/pages/performance/CycleDetailPage'));
const GoalsPage = lazy(() => import('@/pages/performance/GoalsPage'));
const MyReviewsPage = lazy(() => import('@/pages/performance/MyReviewsPage'));
const ReviewFormPage = lazy(() => import('@/pages/performance/ReviewFormPage'));
const SignaturesPage = lazy(() => import('@/pages/hr/SignaturesPage'));
const IntegrationsPage = lazy(() => import('@/pages/settings/IntegrationsPage'));
const NRSMBSSettingsPage = lazy(() => import('@/pages/compliance/NRSMBSSettingsPage'));
const ComplianceDashboardPage = lazy(() => import('@/pages/compliance/ComplianceDashboardPage'));
const SignupPage = lazy(() => import('@/pages/auth/SignupPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage'));
const AcceptInvitePage = lazy(() => import('@/pages/auth/AcceptInvitePage'));
const OnboardingCompletePage = lazy(() => import('@/pages/onboarding/OnboardingCompletePage'));
// Client/Vendor self-service portals (P04) — standalone, no app auth guard.
const InvoicePublicViewPage = lazy(() => import('@/pages/portal/public/InvoicePublicViewPage'));
const ClientPortalLoginPage = lazy(() => import('@/pages/portal/client/ClientPortalLoginPage'));
const ClientPortalDashboardPage = lazy(
  () => import('@/pages/portal/client/ClientPortalDashboardPage'),
);
const ClientInvoiceListPage = lazy(() => import('@/pages/portal/client/ClientInvoiceListPage'));
const VendorPortalLoginPage = lazy(() => import('@/pages/portal/vendor/VendorPortalLoginPage'));
const VendorPortalDashboardPage = lazy(
  () => import('@/pages/portal/vendor/VendorPortalDashboardPage'),
);
const WorkflowRecipesPage = lazy(() => import('@/pages/settings/WorkflowRecipesPage'));
const KnowledgeBasePage = lazy(() => import('@/pages/ai/KnowledgeBasePage'));
const ContactGraphPage = lazy(() => import('@/pages/crm/ContactGraphPage'));
const ContactsListPage = lazy(() => import('@/pages/crm/ContactsListPage'));
const CRMKanbanPage = lazy(() => import('@/pages/crm/CRMKanbanPage'));
const ContactDetailPage = lazy(() => import('@/pages/crm/ContactDetailPage'));
const AccountDetailPage = lazy(() => import('@/pages/crm/AccountDetailPage'));

// Hub Pages — consolidated tab-based navigation
const FinanceHubPage = lazy(() => import('@/pages/finance/FinanceHubPage'));
const PeopleHubPage = lazy(() => import('@/pages/people/PeopleHubPage'));
const PayrollHubPage = lazy(() => import('@/pages/payroll/PayrollHubPage'));
const PayrollGLReconPage = lazy(() => import('@/pages/payroll/PayrollGLReconPage'));
const DisbursementCenterPage = lazy(() => import('@/pages/payroll/DisbursementCenterPage'));
const WHTRemittancePage = lazy(() => import('@/pages/payroll/WHTRemittancePage'));
const FacilityHubPage = lazy(() => import('@/pages/facility/FacilityHubPage'));
const GroupHubPage = lazy(() => import('@/pages/group/GroupHubPage'));
const IntelligenceHubPage = lazy(() => import('@/pages/ai/IntelligenceHubPage'));
// Reports Hub — unified report launcher + the two new gap reports.
const ReportsHubPage = lazy(() => import('@/pages/reports/ReportsHubPage'));
const ReadinessReportPage = lazy(() => import('@/pages/reports/ReadinessReportPage'));
const ComplianceCalendarPage = lazy(() => import('@/pages/reports/ComplianceCalendarPage'));
const ReportRunnerPage = lazy(() => import('@/pages/reports/ReportRunnerPage'));
const HelpPage = lazy(() => import('@/pages/help/HelpPage'));
const HelpCenterPage = lazy(() => import('@/pages/help/HelpCenterPage'));
const OpsHubMovingPage = lazy(() => import('@/pages/help/OpsHubMovingPage'));
// ALMOST-items build — Nigeria market entry
const ExpenseClaimsPage = lazy(() => import('@/pages/hr/ExpenseClaimsPage'));
const MyExpenseClaimsPage = lazy(() => import('@/pages/hr/MyExpenseClaimsPage'));
const DocumentIntelligencePage = lazy(() => import('@/pages/hr/DocumentIntelligencePage'));
const CurrencyRatesPage = lazy(() => import('@/pages/settings/CurrencyRatesPage'));
const DataSubjectRequestsPage = lazy(() => import('@/pages/settings/DataSubjectRequestsPage'));
const AccountantPortalPage = lazy(() => import('@/pages/portal/AccountantPortalPage'));
const TCCWizardPage = lazy(() => import('@/pages/public/TCCWizardPage'));

const LazyFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
    <CircularProgress />
  </Box>
);
import { useAuthStore } from '@/stores/authStore';
import CompanyFormatsProvider from '@/components/CompanyFormatsProvider';
import GlobalUpgradeModal from '@/components/GlobalUpgradeModal';
import { useImpersonationHandoff } from '@/hooks/useImpersonationHandoff';
import { usePermission } from '@/hooks/usePermission';

import '@/i18n/config';
import { useSessionValidator } from '@/hooks/useSessionValidator';

// Re-exported so Sidebar.tsx keeps its existing import path.
// eslint-disable-next-line react-refresh/only-export-components
export { setCompanySwitchInProgress } from '@/state/companySwitchState';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true,
      staleTime: 60_000, // 60 seconds
      retry: 1,
    },
  },
});

/**
 * Validate persisted auth state on app startup.
 * If the cookie has expired but localStorage still says "authenticated",
 * this clears the stale state and forces re-login.
 * Also loads the group's color palette setting.
 */

function ModuleRoute({ moduleKey, children }: { moduleKey: string; children: React.ReactNode }) {
  const enabledModules = useAuthStore((s) => s.getEnabledModules());
  if (!enabledModules.includes(moduleKey)) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
}

function useResolvedHomepage(): string {
  const user = useAuthStore((s) => s.user);
  const companies = useAuthStore((s) => s.companies);
  const activeCompanyId = useAuthStore((s) => s.activeCompanyId);
  const platformRole = useAuthStore((s) => s.platformRole);
  const { hasPermission } = usePermission();

  // Platform admins with NO tenant company at all land on the Platform Console
  // shim — there's no tenant context to render. But a platform admin who is
  // ALSO a tenant member (e.g. the founder) belongs in the tenant app: fall
  // through and resolve their tenant homepage, defaulting the active company to
  // their first membership when the JWT hasn't pinned one yet.
  if (platformRole && companies.length === 0) return '/platform/overview';
  const effectiveCompanyId = activeCompanyId ?? companies[0]?.company_id;
  const activeCompany = companies.find((c) => c.company_id === effectiveCompanyId);
  const prefs = user?.preferences ?? {};
  const hp = prefs.default_homepage;
  if (hp === 'module') return `/${prefs.default_module || 'dashboard'}`;
  if (hp === 'portal') return '/portal';

  // Permission-based routing: settings.read is only granted to admin-level roles
  // (Administrator, Company Admin, Group Admin, CFO, CHRO, etc.). Employee-level
  // roles (Staff, Employee, Viewer, HR Assistant) do not hold it, so they land
  // on the self-service portal instead of the admin dashboard.
  // This replaces the fragile role_name === 'Staff' string comparison which broke
  // for any RBAC v2 specialist role (Employee, Line Manager, etc.).
  const isPortalUser = !hasPermission('settings.read');
  if (isPortalUser) return '/portal';

  // Redirect to /setup until wizard is explicitly marked complete on the server.
  // setup_completed is set server-side — localStorage state is not authoritative.
  if (activeCompany && activeCompany.setup_completed !== true) return '/setup';

  return '/dashboard';
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isOnboarding = useAuthStore((s) => s.isOnboarding());
  const homepage = useResolvedHomepage();
  if (isAuthenticated) {
    if (isOnboarding) return <Navigate to="/my-onboarding" replace />;
    return <Navigate to={homepage} replace />;
  }
  return <>{children}</>;
}

/**
 * Route guard that redirects onboarding employees away from the full app.
 * The old "needs setup" redirect has been removed — signup now finishes
 * everything required before the first login, so there's no partial state
 * to gate on. The Setup Wizard at /setup is still available but voluntary.
 */
function FullAppGuard({ children }: { children: React.ReactNode }) {
  const isOnboarding = useAuthStore((s) => s.isOnboarding());
  const mustChangePassword = useAuthStore((s) => s.user?.must_change_password);
  const location = useLocation();
  if (isOnboarding) return <Navigate to="/my-onboarding" replace />;
  if (mustChangePassword && !location.pathname.startsWith('/profile')) {
    return <Navigate to="/profile" state={{ tab: 2, forcePasswordChange: true }} replace />;
  }
  return <>{children}</>;
}

function DefaultRedirect() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const authStatus = useAuthStore((s) => s.authStatus);
  const isOnboarding = useAuthStore((s) => s.isOnboarding());
  const homepage = useResolvedHomepage();
  const { hasPermission } = usePermission();
  // Mirror useResolvedHomepage's fallback so both resolve the same company.
  const activeCompany = useAuthStore((s) => {
    const id = s.activeCompanyId ?? s.companies[0]?.company_id;
    return s.companies.find((c) => c.company_id === id);
  });

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (isOnboarding) return <Navigate to="/my-onboarding" replace />;
  // During auth 'pending', companies[] is empty — wait before resolving homepage.
  if (authStatus !== 'ready') return null;

  const isAdmin = activeCompany !== undefined && hasPermission('settings.read');
  if (isAdmin && activeCompany?.setup_completed !== true && homepage === '/dashboard') {
    return <Navigate to="/setup" replace />;
  }
  return <Navigate to={homepage} replace />;
}

/**
 * Platform admin route guard. Non-platform users are redirected to /dashboard.
 * Server-side `require_platform_role` is the authoritative gate; this just
 * keeps the UI from flashing for users without the role.
 */
function PlatformRoute({ children }: { children: React.ReactNode }) {
  const platformRole = useAuthStore((s) => s.platformRole);
  if (!platformRole) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

export default function App() {
  // Cross-domain impersonation handoff (Sprint 3b). When the URL carries
  // ?impersonation=<code>, this hook exchanges the one-time code for a
  // tenant session before anything else renders. A successful exchange
  // reloads the page with a fresh access_token cookie; failure surfaces
  // in-place via the returned `error` string (not wired to UI yet — the
  // user is rare enough that a console warning + query param removal is
  // acceptable for now).
  const handoff = useImpersonationHandoff();
  useSessionValidator();
  useEffect(() => {
    // Consent-gated analytics boot. Safe to call unconditionally —
    // init only happens once the user accepts analytics cookies.
    import('@/lib/analytics').then(({ bootAnalytics }) => bootAnalytics());
  }, []);
  useEffect(() => {
    if (handoff.status === 'error' && handoff.error) {
      // Impersonation handoff errors are visible in the UI via handoff.error
    }
  }, [handoff.status, handoff.error]);
  const darkMode = useUIStore((s) => s.darkMode);
  const groupPaletteKey = useUIStore((s) => s.groupPaletteKey);
  // Active company's brand colour drives the primary accent (contrast-safe).
  const brandColor = useAuthStore((s) => s.getActiveCompany()?.company_colour);
  // Resolved theme from the console-managed cascade (platform default → tenant
  // override). `style` drives the card aesthetic; `palette` (when set) overrides
  // the group palette key; `dark_default` seeds dark mode when the user hasn't
  // explicitly chosen one this session.
  const companyTheme = useAuthStore((s) => s.getActiveCompany()?.theme);
  const effectivePalette = companyTheme?.palette ?? groupPaletteKey;
  const effectiveDark = darkMode || (companyTheme?.dark_default ?? false);
  const theme = useMemo(
    () => getTheme(effectiveDark ? 'dark' : 'light', effectivePalette, brandColor, companyTheme?.style ?? 'balanced'),
    [effectiveDark, effectivePalette, brandColor, companyTheme?.style],
  );

  return (
    <ErrorBoundary>
    <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <SnackbarProvider
          maxSnack={4}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          preventDuplicate
          autoHideDuration={4000}
        />
        <CookieConsent />
        <CompanyFormatsProvider />
        <GlobalUpgradeModal />
        <BrowserRouter>
          <Routes>
            {/* Public routes */}
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <LoginPage />
                </PublicRoute>
              }
            />

            {/* WTH — Auth0 OAuth callback. Public route; the page itself
                completes the code-for-token exchange against the backend. */}
            <Route path="/auth/auth0/callback" element={<Auth0CallbackPage />} />

            <Route
              path="/signup"
              element={
                <PublicRoute>
                  <Suspense fallback={<LazyFallback />}>
                    <SignupPage />
                  </Suspense>
                </PublicRoute>
              }
            />

            <Route
              path="/forgot-password"
              element={
                <PublicRoute>
                  <Suspense fallback={<LazyFallback />}>
                    <ForgotPasswordPage />
                  </Suspense>
                </PublicRoute>
              }
            />

            <Route
              path="/reset-password"
              element={
                <PublicRoute>
                  <Suspense fallback={<LazyFallback />}>
                    <ResetPasswordPage />
                  </Suspense>
                </PublicRoute>
              }
            />

            {/* Staff invite-acceptance — public, no auth required; accepts ?token= param */}
            <Route
              path="/accept-invite"
              element={
                <Suspense fallback={<LazyFallback />}>
                  <AcceptInvitePage />
                </Suspense>
              }
            />

            {/* GAP-014 — self-service onboarding; public, tokenized (?token=). The
                page manages its own scoped portal cookie. */}
            <Route
              path="/onboarding/complete"
              element={
                <Suspense fallback={<LazyFallback />}>
                  <OnboardingCompletePage />
                </Suspense>
              }
            />

            {/* Client/Vendor self-service portals (P04) — standalone, no app
                auth guard; each portal manages its own cookie session. */}
            <Route
              path="/invoice/view/:token"
              element={
                <Suspense fallback={<LazyFallback />}>
                  <InvoicePublicViewPage />
                </Suspense>
              }
            />
            {/* TCC Wizard — public, no auth required */}
            <Route
              path="/tcc-check"
              element={
                <Suspense fallback={<LazyFallback />}>
                  <TCCWizardPage />
                </Suspense>
              }
            />
            <Route
              path="/client-portal/login"
              element={
                <Suspense fallback={<LazyFallback />}>
                  <ClientPortalLoginPage />
                </Suspense>
              }
            />
            <Route
              path="/client-portal/accept-invite"
              element={
                <Suspense fallback={<LazyFallback />}>
                  <ClientPortalLoginPage />
                </Suspense>
              }
            />
            <Route
              path="/client-portal"
              element={
                <Suspense fallback={<LazyFallback />}>
                  <ClientPortalDashboardPage />
                </Suspense>
              }
            />
            <Route
              path="/client-portal/invoices"
              element={
                <Suspense fallback={<LazyFallback />}>
                  <ClientInvoiceListPage />
                </Suspense>
              }
            />
            <Route
              path="/vendor-portal/login"
              element={
                <Suspense fallback={<LazyFallback />}>
                  <VendorPortalLoginPage />
                </Suspense>
              }
            />
            <Route
              path="/vendor-portal/accept-invite"
              element={
                <Suspense fallback={<LazyFallback />}>
                  <VendorPortalLoginPage />
                </Suspense>
              }
            />
            <Route
              path="/vendor-portal"
              element={
                <Suspense fallback={<LazyFallback />}>
                  <VendorPortalDashboardPage />
                </Suspense>
              }
            />

            {/* Onboarding-only routes — minimal shell, no sidebar */}
            <Route
              element={
                <ProtectedRoute>
                  <OnboardingShell />
                </ProtectedRoute>
              }
            >
              <Route path="/my-onboarding" element={<MyOnboardingPage />} />
            </Route>

            {/* Protected routes inside AppShell — blocked for onboarding users */}
            <Route
              element={
                <ProtectedRoute>
                  <FullAppGuard>
                    <AppShell />
                  </FullAppGuard>
                </ProtectedRoute>
              }
            >
              <Route path="/dashboard" element={<ProtectedRoute requiredPermission="dashboard.read"><Suspense fallback={<LazyFallback />}><DashboardPage /></Suspense></ProtectedRoute>} />
              <Route path="/approvals" element={<ProtectedRoute requiredPermission="dashboard.read"><Suspense fallback={<LazyFallback />}><MyApprovalsPage /></Suspense></ProtectedRoute>} />
              <Route path="/help" element={<Suspense fallback={<LazyFallback />}><HelpPage /></Suspense>} />
              {/* Static help slugs registered before the generic :slug catch-all so the
                  dedicated page renders instead of HelpPage's slug→article lookup. */}
              <Route path="/help/ops-hub-moving" element={<Suspense fallback={<LazyFallback />}><OpsHubMovingPage /></Suspense>} />
              <Route path="/help/:slug" element={<Suspense fallback={<LazyFallback />}><HelpPage /></Suspense>} />
              <Route path="/help-center" element={<Suspense fallback={<LazyFallback />}><HelpCenterPage /></Suspense>} />
              <Route path="/setup" element={<ProtectedRoute requiredPermission="settings.read"><Suspense fallback={<LazyFallback />}><SetupWizardPage /></Suspense></ProtectedRoute>} />
              <Route path="/getting-started" element={<ProtectedRoute requiredPermission="settings.read"><Suspense fallback={<LazyFallback />}><GettingStartedPage /></Suspense></ProtectedRoute>} />

              {/* ── Finance Hub ── */}
              <Route path="/finance" element={<ProtectedRoute requiredPermission={['accounting.read', 'accounting.revenue.read', 'accounting.expense.read', 'accounting.reports.read']}><ModuleRoute moduleKey="accounting"><Suspense fallback={<LazyFallback />}><FinanceHubPage /></Suspense></ModuleRoute></ProtectedRoute>} />

              {/* Legacy accounting redirects → Finance hub (grouped) */}
              <Route path="/accounting/revenue" element={<Navigate to="/finance#transactions.revenue" replace />} />
              <Route path="/accounting/expenses" element={<Navigate to="/finance#transactions.expenses" replace />} />
              <Route path="/accounting/invoices" element={<Navigate to="/finance#transactions.invoices" replace />} />
              <Route path="/accounting/recurring" element={<Navigate to="/finance#transactions.recurring" replace />} />
              <Route path="/accounting/journal-entries" element={<Navigate to="/finance#ledger.journals" replace />} />
              <Route path="/accounting/general-ledger" element={<Navigate to="/finance#ledger.general-ledger" replace />} />
              <Route path="/accounting/chart-of-accounts" element={<Navigate to="/finance#ledger.coa" replace />} />
              <Route path="/accounting/reports" element={<Navigate to="/finance#reports.financial" replace />} />
              <Route path="/accounting/aged-reports" element={<Navigate to="/finance#reports.aged-reports" replace />} />
              <Route path="/accounting/analysis" element={<Navigate to="/finance#reports.analysis" replace />} />
              <Route path="/accounting/bank-feeds" element={<Navigate to="/finance#banking.bank-feeds" replace />} />
              <Route path="/accounting/period-close" element={<Navigate to="/finance#banking.period-close" replace />} />
              <Route path="/budget" element={<Navigate to="/finance#budget.overview" replace />} />
              <Route path="/budget/variance" element={<Navigate to="/finance#budget.variance" replace />} />

              {/* ── People Hub ── */}
              <Route path="/people" element={<ProtectedRoute requiredPermission={['hr.read', 'hr.employees.read']}><ModuleRoute moduleKey="hr"><Suspense fallback={<LazyFallback />}><PeopleHubPage /></Suspense></ModuleRoute></ProtectedRoute>} />

              {/* Detail routes — stay standalone */}
              <Route path="/hr/employees/:id" element={<ProtectedRoute requiredPermission="hr.employees.read"><ModuleRoute moduleKey="hr"><Suspense fallback={<LazyFallback />}><EmployeeDetailPage /></Suspense></ModuleRoute></ProtectedRoute>} />
              <Route path="/hr/my-info" element={<Navigate to="/profile" replace />} />
              <Route path="/hr/recruitment/applications/:appId" element={<ProtectedRoute requiredPermission="hr.recruitment.read"><ModuleRoute moduleKey="recruitment"><Suspense fallback={<LazyFallback />}><ApplicantDetailPage /></Suspense></ModuleRoute></ProtectedRoute>} />
              <Route path="/hr/departments" element={<ProtectedRoute requiredPermission={['hr.departments.read', 'hr.departments.write']}><ModuleRoute moduleKey="hr"><DepartmentSettingsPage /></ModuleRoute></ProtectedRoute>} />
              <Route path="/hr/performance" element={<ProtectedRoute requiredPermission={['performance.reviews.read', 'performance.reviews.write']}><ModuleRoute moduleKey="performance"><Suspense fallback={<LazyFallback />}><PerformanceReviewsPage /></Suspense></ModuleRoute></ProtectedRoute>} />
              <Route path="/performance/cycles/:id" element={<ProtectedRoute requiredPermission="performance.reviews.read"><ModuleRoute moduleKey="performance"><Suspense fallback={<LazyFallback />}><CycleDetailPage /></Suspense></ModuleRoute></ProtectedRoute>} />
              <Route path="/performance/goals" element={<ProtectedRoute requiredPermission={['performance.goals.read', 'performance.goals.write']}><ModuleRoute moduleKey="performance"><Suspense fallback={<LazyFallback />}><GoalsPage /></Suspense></ModuleRoute></ProtectedRoute>} />
              <Route path="/performance/my-reviews" element={<Suspense fallback={<LazyFallback />}><MyReviewsPage /></Suspense>} />
              <Route path="/performance/review/:id" element={<Suspense fallback={<LazyFallback />}><ReviewFormPage /></Suspense>} />
              <Route path="/employee/signatures" element={<ProtectedRoute requiredPermission="portal.read"><Suspense fallback={<LazyFallback />}><SignaturesPage /></Suspense></ProtectedRoute>} />

              {/* Legacy people redirects → People hub (grouped) */}
              <Route path="/hr/employees" element={<Navigate to="/people#workforce.employees" replace />} />
              <Route path="/employee/directory" element={<Navigate to="/people#employees" replace />} />
              <Route path="/hr/org-chart" element={<Navigate to="/people#org-chart" replace />} />
              <Route path="/hr/recruitment" element={<Navigate to="/people#hiring.recruitment" replace />} />
              <Route path="/hr/onboarding" element={<Navigate to="/people#hiring.onboarding" replace />} />
              <Route path="/hr/leave" element={<Navigate to="/people#management.leave" replace />} />
              <Route path="/performance" element={<Navigate to="/people#management.performance" replace />} />
              <Route path="/hr/training" element={<Navigate to="/people#development.training" replace />} />
              <Route path="/hr/documents" element={<Navigate to="/people#documents" replace />} />
              <Route path="/hr/compliance" element={<Navigate to="/compliance" replace />} />

              {/* ── Payroll Hub ── */}
              <Route path="/payroll" element={<ProtectedRoute requiredPermission={['payroll.read', 'payroll.write']}><ModuleRoute moduleKey="payroll"><Suspense fallback={<LazyFallback />}><PayrollHubPage /></Suspense></ModuleRoute></ProtectedRoute>} />

              {/* Legacy payroll redirects → Payroll hub */}
              <Route path="/payroll/remittances" element={<Navigate to="/payroll#remittances" replace />} />
              <Route path="/payroll/garnishments" element={<Navigate to="/payroll#garnishments" replace />} />
              <Route path="/payroll/calendar" element={<Navigate to="/payroll#calendar" replace />} />
              <Route path="/payroll/gl-reconciliation" element={<ProtectedRoute requiredPermission="payroll.read"><ModuleRoute moduleKey="payroll"><Suspense fallback={<LazyFallback />}><PayrollGLReconPage /></Suspense></ModuleRoute></ProtectedRoute>} />
              <Route path="/payroll/wht-remittance" element={<ProtectedRoute requiredPermission="payroll.read"><ModuleRoute moduleKey="payroll"><Suspense fallback={<LazyFallback />}><WHTRemittancePage /></Suspense></ModuleRoute></ProtectedRoute>} />
              <Route path="/payroll/disbursement-center" element={<ProtectedRoute requiredPermission="payroll.read"><ModuleRoute moduleKey="payroll"><Suspense fallback={<LazyFallback />}><DisbursementCenterPage /></Suspense></ModuleRoute></ProtectedRoute>} />

              {/* ── Facility Hub ── */}
              <Route path="/facility" element={<ProtectedRoute requiredPermission={['tenants.read', 'tenants.write']}><ModuleRoute moduleKey="tenants"><Suspense fallback={<LazyFallback />}><FacilityHubPage /></Suspense></ModuleRoute></ProtectedRoute>} />

              {/* Legacy facility/tenant redirects → Facility hub */}
              <Route path="/tenants" element={<Navigate to="/facility#tenants" replace />} />
              <Route path="/tenants/leases" element={<Navigate to="/facility#leases" replace />} />
              <Route path="/tenants/rent-tracking" element={<Navigate to="/facility#rent" replace />} />
              <Route path="/tenants/locations" element={<Navigate to="/facility#locations" replace />} />
              <Route path="/tenants/buildings" element={<Navigate to="/facility#locations" replace />} />

              {/* ── Group Hub ── */}
              <Route path="/group" element={<ProtectedRoute requiredPermission="group.companies.read"><ModuleRoute moduleKey="group"><Suspense fallback={<LazyFallback />}><GroupHubPage /></Suspense></ModuleRoute></ProtectedRoute>} />

              {/* Legacy group redirects → Group hub */}
              <Route path="/group/dashboard" element={<Navigate to="/group#dashboard" replace />} />
              <Route path="/group/companies" element={<Navigate to="/group#companies" replace />} />
              <Route path="/group/consolidated" element={<Navigate to="/group#consolidation" replace />} />
              <Route path="/group/intercompany" element={<Navigate to="/group#intercompany" replace />} />
              <Route path="/group/cost-allocation" element={<Navigate to="/group#cost-allocation" replace />} />
              <Route path="/group/exchange-rates" element={<Navigate to="/group#exchange-rates" replace />} />

              {/* ── Intelligence Hub ── */}
              <Route path="/intelligence" element={<ProtectedRoute requiredPermission="ai.chat"><ModuleRoute moduleKey="ai"><Suspense fallback={<LazyFallback />}><IntelligenceHubPage /></Suspense></ModuleRoute></ProtectedRoute>} />

              {/* Legacy AI redirects → Intelligence hub */}
              <Route path="/ai/chat" element={<Navigate to="/intelligence#chat" replace />} />
              <Route path="/ai/reminders" element={<Navigate to="/intelligence#reminders" replace />} />
              <Route path="/ai/settings" element={<Navigate to="/intelligence#settings" replace />} />
              <Route path="/ai/analytics" element={<Navigate to="/intelligence#analytics" replace />} />
              <Route path="/ai/knowledge-base" element={<ProtectedRoute requiredPermission="ai.settings"><ModuleRoute moduleKey="ai"><Suspense fallback={<LazyFallback />}><KnowledgeBasePage /></Suspense></ModuleRoute></ProtectedRoute>} />

              {/* ── Reports Hub ── (cross-module launcher; per-card gating in the registry) */}
              <Route path="/reports" element={<ProtectedRoute requiredPermission={['accounting.reports.read', 'payroll.read', 'hr.employees.read', 'audit.read']}><Suspense fallback={<LazyFallback />}><ReportsHubPage /></Suspense></ProtectedRoute>} />
              <Route path="/reports/readiness" element={<ProtectedRoute requiredPermission={['payroll.read', 'settings.read']}><Suspense fallback={<LazyFallback />}><ReadinessReportPage /></Suspense></ProtectedRoute>} />
              <Route path="/reports/compliance-calendar" element={<ProtectedRoute requiredPermission={['accounting.reports.read', 'payroll.read', 'mbs.compliance.read']}><Suspense fallback={<LazyFallback />}><ComplianceCalendarPage /></Suspense></ProtectedRoute>} />
              <Route path="/reports/runner" element={<ProtectedRoute requiredPermission={['accounting.reports.read', 'hr.employees.read', 'payroll.read']}><Suspense fallback={<LazyFallback />}><ReportRunnerPage /></Suspense></ProtectedRoute>} />

              {/* Settings Hub — unified settings page */}
              <Route path="/settings" element={<ProtectedRoute requiredPermission="settings.read"><Suspense fallback={<LazyFallback />}><SettingsHubPage /></Suspense></ProtectedRoute>} />

              {/* Settings (individual routes — kept for backward compat & direct linking) */}
              <Route path="/settings/group" element={<ProtectedRoute requiredPermission="settings.roles.read"><ModuleRoute moduleKey="group"><GroupSettingsPage /></ModuleRoute></ProtectedRoute>} />
              <Route path="/settings/company" element={<ProtectedRoute requiredPermission="settings.company.read"><CompanySettingsPage /></ProtectedRoute>} />
              <Route path="/settings/ai" element={<ProtectedRoute requiredPermission="settings.company.read"><Suspense fallback={<LazyFallback />}><AISettingsPage /></Suspense></ProtectedRoute>} />
              {/* /settings/storage moved to console.anoorehr.com/storage — STORAGE_BACKEND is platform-scope, not tenant-scope. */}
              <Route path="/settings/storage" element={<Navigate to="/settings" replace />} />
              <Route path="/settings/users" element={<ProtectedRoute requiredPermission="settings.users.read"><UsersPage /></ProtectedRoute>} />
              <Route path="/settings/roles" element={<ProtectedRoute requiredPermission="settings.roles.read"><RolesPage /></ProtectedRoute>} />
              <Route path="/settings/roles/:roleId" element={<ProtectedRoute requiredPermission="settings.roles.read"><RoleDetailPage /></ProtectedRoute>} />
              <Route path="/settings/reference-data" element={<ProtectedRoute requiredPermission="settings.company.read"><ReferenceDataPage /></ProtectedRoute>} />
              <Route path="/settings/department" element={<ProtectedRoute requiredPermission="settings.company.read"><DepartmentSettingsPage /></ProtectedRoute>} />
              <Route path="/settings/holidays" element={<ProtectedRoute requiredPermission="settings.holidays.read"><HolidaysPage /></ProtectedRoute>} />
              <Route path="/settings/company-holidays" element={<Navigate to="/settings/holidays#company" replace />} />
              <Route path="/settings/announcements" element={<ProtectedRoute requiredPermission="portal.write"><AnnouncementsPage /></ProtectedRoute>} />
              <Route path="/settings/onboarding" element={<ProtectedRoute requiredPermission="hr.onboarding.write"><OnboardingSettingsPage /></ProtectedRoute>} />
              <Route path="/settings/recruitment" element={<ProtectedRoute requiredPermission="hr.recruitment.write"><Suspense fallback={<LazyFallback />}><RecruitmentSettingsPage /></Suspense></ProtectedRoute>} />
              <Route path="/settings/tracking-categories" element={<ProtectedRoute requiredPermission="settings.company.read"><Suspense fallback={<LazyFallback />}><TrackingCategoriesPage /></Suspense></ProtectedRoute>} />
              <Route path="/settings/personal" element={<Navigate to="/profile" state={{ tab: 6 }} replace />} />
              <Route path="/settings/notifications" element={<ProtectedRoute requiredPermission="settings.company.read"><NotificationsSettingsPage /></ProtectedRoute>} />
              {/* Legacy routes — redirect to unified settings */}
              <Route path="/settings/notification-preferences" element={<Navigate to="/settings#company.notifications" replace />} />
              <Route path="/settings/notification-templates" element={<Navigate to="/settings/notifications#templates" replace />} />
              <Route path="/settings/email" element={<Navigate to="/settings/notifications#email-delivery" replace />} />
              <Route path="/settings/import" element={<ProtectedRoute requiredPermission="settings.company.write"><Suspense fallback={<LazyFallback />}><DataImportPage /></Suspense></ProtectedRoute>} />
              <Route path="/settings/data-migration" element={<ProtectedRoute requiredPermission="settings.company.write"><Suspense fallback={<LazyFallback />}><DataMigrationWizardPage /></Suspense></ProtectedRoute>} />
              <Route path="/settings/modules" element={<ProtectedRoute requiredPermission="settings.modules.read"><ModuleConfigPage /></ProtectedRoute>} />
              <Route path="/settings/automations" element={<ProtectedRoute requiredPermission="settings.company.write"><Suspense fallback={<LazyFallback />}><AutomationsPage /></Suspense></ProtectedRoute>} />
              <Route path="/settings/workflows" element={<ProtectedRoute requiredPermission="settings.company.write"><Suspense fallback={<LazyFallback />}><WorkflowsPage /></Suspense></ProtectedRoute>} />
              <Route path="/settings/workflows/builder" element={<ProtectedRoute requiredPermission="settings.company.write"><Suspense fallback={<LazyFallback />}><WorkflowBuilderPage /></Suspense></ProtectedRoute>} />
              <Route path="/settings/workflows/:id/builder" element={<ProtectedRoute requiredPermission="settings.company.write"><Suspense fallback={<LazyFallback />}><WorkflowBuilderPage /></Suspense></ProtectedRoute>} />
              <Route path="/settings/integrations" element={<ProtectedRoute requiredPermission="settings.company.write"><Suspense fallback={<LazyFallback />}><IntegrationsPage /></Suspense></ProtectedRoute>} />
              <Route path="/settings/compliance/nrs-mbs" element={<ProtectedRoute requiredPermission="mbs.compliance.write"><Suspense fallback={<LazyFallback />}><NRSMBSSettingsPage /></Suspense></ProtectedRoute>} />
              {/* ALMOST-items — Nigeria market entry */}
              <Route path="/hr/expense-claims" element={<ProtectedRoute requiredPermission="hr.employees.read"><Suspense fallback={<LazyFallback />}><ExpenseClaimsPage /></Suspense></ProtectedRoute>} />
              <Route path="/hr/expense-claims/mine" element={<ProtectedRoute requiredPermission="hr.employees.read"><Suspense fallback={<LazyFallback />}><MyExpenseClaimsPage /></Suspense></ProtectedRoute>} />
              <Route path="/hr/document-intelligence" element={<ProtectedRoute requiredPermission="hr.employees.write"><ModuleRoute moduleKey="ai"><Suspense fallback={<LazyFallback />}><DocumentIntelligencePage /></Suspense></ModuleRoute></ProtectedRoute>} />
              <Route path="/settings/currency-rates" element={<ProtectedRoute requiredPermission="settings.company.read"><Suspense fallback={<LazyFallback />}><CurrencyRatesPage /></Suspense></ProtectedRoute>} />
              <Route path="/settings/data-subject-requests" element={<ProtectedRoute requiredPermission="settings.admin"><Suspense fallback={<LazyFallback />}><DataSubjectRequestsPage /></Suspense></ProtectedRoute>} />
              <Route path="/portal/accountant" element={<ProtectedRoute requiredPermission="settings.company.read"><Suspense fallback={<LazyFallback />}><AccountantPortalPage /></Suspense></ProtectedRoute>} />
              <Route path="/compliance" element={<ProtectedRoute requiredPermission="mbs.compliance.read"><Suspense fallback={<LazyFallback />}><ComplianceDashboardPage /></Suspense></ProtectedRoute>} />
              <Route path="/settings/recipes" element={<ProtectedRoute requiredPermission="settings.company.write"><Suspense fallback={<LazyFallback />}><WorkflowRecipesPage /></Suspense></ProtectedRoute>} />

              {/* Audit (core — requires audit.read) */}
              <Route path="/audit" element={<ProtectedRoute requiredPermission="audit.read"><AuditLogPage /></ProtectedRoute>} />

              {/* Portal & Notifications (core — no module guard) */}
              <Route path="/portal" element={<PortalPage />} />
              <Route path="/portal/announcements/:id" element={<AnnouncementDetailPage />} />
              <Route path="/notifications" element={<NotificationsPage />} />

              {/* Messaging */}
              <Route path="/messaging/*" element={<ProtectedRoute requiredPermission={['comms.read', 'comms.write']}><ModuleRoute moduleKey="comms"><MessagingPage /></ModuleRoute></ProtectedRoute>} />

              {/* Comms — Compliance Dashboard + Deliverability Board */}
              <Route path="/comms/compliance" element={<ProtectedRoute requiredPermission={['comms.read', 'comms.write']}><Suspense fallback={<LazyFallback />}><CommsComplianceDashboardPage /></Suspense></ProtectedRoute>} />
              <Route path="/comms/deliverability" element={<ProtectedRoute requiredPermission={['comms.read', 'comms.write']}><Suspense fallback={<LazyFallback />}><DeliverabilityPage /></Suspense></ProtectedRoute>} />

              {/* CRM */}
              <Route path="/crm/contacts" element={<ProtectedRoute requiredPermission={['crm.read']}><Suspense fallback={<LazyFallback />}><ContactsListPage /></Suspense></ProtectedRoute>} />
              <Route path="/crm/contacts/:id" element={<ProtectedRoute requiredPermission={['crm.read']}><Suspense fallback={<LazyFallback />}><ContactDetailPage /></Suspense></ProtectedRoute>} />
              <Route path="/crm/kanban" element={<ProtectedRoute requiredPermission={['crm.read']}><Suspense fallback={<LazyFallback />}><CRMKanbanPage /></Suspense></ProtectedRoute>} />
              <Route path="/crm/accounts/:id" element={<ProtectedRoute requiredPermission={['crm.read']}><Suspense fallback={<LazyFallback />}><AccountDetailPage /></Suspense></ProtectedRoute>} />
              <Route path="/crm/graph" element={<ProtectedRoute requiredPermission={['crm.read']}><Suspense fallback={<LazyFallback />}><ContactGraphPage /></Suspense></ProtectedRoute>} />
              <Route path="/crm" element={<Navigate to="/crm/contacts" replace />} />

              {/* Pulse / Marketing — retired from tenant app; shown as Coming 2027 in /settings/modules */}
              <Route path="/pulse/sidekick/*" element={<Navigate to="/settings/modules" replace />} />
              <Route path="/marketing/*" element={<Navigate to="/settings/modules" replace />} />

              {/* Profile */}
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/profile/payment-details" element={<PaymentDetailsPage />} />
            </Route>

            {/* Platform Admin — superuser-only SaaS operator console.
                Separate shell (dark sidebar), distinct guard (platform_role). */}
            <Route
              element={
                <ProtectedRoute>
                  <PlatformRoute>
                    <PlatformShell />
                  </PlatformRoute>
                </ProtectedRoute>
              }
            >
              <Route path="/platform" element={<Navigate to="/platform/overview" replace />} />
              {/* SSO / Identity — platform_owner only. Must be listed BEFORE
                  the /platform/* catch-all so it renders the real page. */}
              <Route
                path="/platform/auth/sso"
                element={<Suspense fallback={<LazyFallback />}><SSOConnectionsPage /></Suspense>}
              />
              {/* Every other /platform/* route in the tenant app renders the
                  MovedToConsolePage — real pages live at console.anoorehr.com. */}
              <Route
                path="/platform/*"
                element={<Suspense fallback={<LazyFallback />}><PlatformMovedToConsolePage /></Suspense>}
              />
            </Route>

            {/* Default redirect — onboarding users go to /my-onboarding */}
            <Route path="*" element={<DefaultRedirect />} />
          </Routes>
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
    </HelmetProvider>
    </ErrorBoundary>
  );
}
